if (!customElements.get('search-form')) {
  class SearchForm extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.is_drawer = !!this.dataset.drawer;
      this.drawer = this.closest('#Search-Drawer');
      this.form = this.querySelector('form');
      this.input = this.querySelector('.search-field');
      this.defaultTab = this.querySelector('.side-panel-content--initial');
      // Do not mistake the initial popular-search list for the results region.
      this.predictiveSearchResults = this.is_drawer ? this.querySelector(':scope > .thb-predictive-search') : this.querySelector('.thb-predictive-search');
      this.form.addEventListener('submit', event => { if (!this.getQuery()) event.preventDefault(); });
      if (this.is_drawer) {
        document.querySelectorAll('.thb-quick-search').forEach(button => {
          button.addEventListener('click', event => {
            event.preventDefault();
            document.body.classList.add('open-cc');
            this.drawer.classList.add('active');
          });
        });
      }
      if (!this.predictiveSearchResults) return;
      this.feedback = ivAsyncUI.feedback(() => this.getSearchResults(this.getQuery()), true);
      this.predictiveSearchResults.prepend(this.feedback.root);
      // Block B: Das Ladesymbol gehoert zum Panel und darf beim Zuruecksetzen nicht verloren gehen.
      this.loadingOverlay = this.predictiveSearchResults.querySelector('.loading-overlay');
      this.input.addEventListener('input', () => {
        // Invalidate immediately, before the debounce interval elapses.
        this.cancelRequest();
        window.clearTimeout(this.inputTimer);
        if (!this.getQuery()) { this.resetResults(); return; }
        this.inputTimer = window.setTimeout(() => this.onChange(), 300);
      });
      this.input.addEventListener('focus', () => this.onChange());
      this.querySelector('.searchform--click-capture')?.addEventListener('click', () => this.close());
      if (this.is_drawer) {
        this.drawerObserver = new MutationObserver(() => {
          if (!this.drawer.classList.contains('active')) {
            window.clearTimeout(this.inputTimer);
            this.cancelRequest();
          }
        });
        this.drawerObserver.observe(this.drawer, { attributes: true, attributeFilter: ['class'] });
      } else {
        this.addEventListener('keydown', event => {
          if (event.key === 'Escape' && !event.defaultPrevented) {
            event.preventDefault();
            this.close();
            this.input.focus({ preventScroll: true });
          }
        });
      }
    }
    disconnectedCallback() { window.clearTimeout(this.inputTimer); this.cancelRequest(); }
    getQuery() { return this.input.value.trim(); }
    onChange() {
      const query = this.getQuery();
      if (!query) { this.resetResults(); return; }
      if (!this.is_drawer) this.open();
      if (this.request?.query === query || (this.completedQuery === query && this.state === 'success')) return;
      this.getSearchResults(query);
    }
    cancelRequest() {
      const old = this.request;
      this.request = null;
      old?.transport.cancel();
      if (this.predictiveSearchResults) {
        this.predictiveSearchResults.classList.remove('loading');
        this.predictiveSearchResults.setAttribute('aria-busy', 'false');
      }
    }
    resetResults() {
      this.cancelRequest();
      this.completedQuery = null;
      this.state = 'idle';
      this.predictiveSearchResults.replaceChildren(...[this.feedback.root, this.loadingOverlay].filter(Boolean));
      this.feedback.set('');
      this.predictiveSearchResults.classList.remove('active');
      if (!this.is_drawer) this.close();
    }
    async getSearchResults(query) {
      if (!query) { this.resetResults(); return; }
      this.cancelRequest();
      window.clearTimeout(this.inputTimer);
      const retryFocusedAtStart = document.activeElement === this.feedback.button;
      if (!this.is_drawer) this.open();
      const results = this.predictiveSearchResults;
      results.classList.add('active', 'loading');
      results.setAttribute('aria-busy', 'true');
      this.state = 'loading';
      this.feedback.set(ivAsyncUI.text('searchLoading'), 'loading', query);
      const url = `${theme.routes.predictive_search_url}?q=${encodeURIComponent(query)}&${encodeURIComponent('resources[type]')}=product,article,query,page&${encodeURIComponent('resources[limit]')}=10&resources[options][fields]=title,product_type,vendor,variants.title,variants.sku&section_id=predictive-search`;
      const request = { query, transport: ivAsyncUI.load(url) };
      this.request = request;
      try {
        const html = await request.transport.promise;
        if (this.request !== request || query !== this.getQuery()) return;
        const section = new DOMParser().parseFromString(html, 'text/html').querySelector('#shopify-section-predictive-search');
        const content = section?.querySelector('.side-panel-content--inner');
        if (!content || !section.querySelector('#search-results-submit')) throw new Error('Invalid predictive-search markup');
        const retryFocused = document.activeElement === this.feedback.button || (retryFocusedAtStart && document.activeElement === document.body);
        this.completedQuery = query;
        this.state = 'success';
        if (retryFocused) this.input.focus({ preventScroll: true });
        results.innerHTML = section.innerHTML;
        results.prepend(this.feedback.root);
        const submit = results.querySelector('#search-results-submit');
        submit.type = 'button';
        if (!this.is_drawer) submit.classList.remove('button');
        submit.addEventListener('click', () => this.form.requestSubmit());
        // Der Leerzustand hat jetzt eigenen Inhalt (drei Wege), deshalb entscheidet das Merkmal.
        const leer = Boolean(content.querySelector('[data-predictive-empty]'));
        this.feedback.set(ivAsyncUI.text(leer ? 'searchEmpty' : 'searchReady'), 'success', query);
      } catch (error) {
        if (this.request !== request || query !== this.getQuery()) return;
        this.state = 'error';
        let message = ivAsyncUI.text('searchError');
        if (this.completedQuery) message += ' ' + ivAsyncUI.text('searchPrevious').replace('{query}', this.completedQuery);
        this.feedback.set(message, 'error', query);
      } finally {
        if (this.request === request) {
          this.request = null;
          results.classList.remove('loading');
          results.setAttribute('aria-busy', 'false');
        }
      }
    }
    close() {
      window.clearTimeout(this.inputTimer);
      this.cancelRequest();
      this.predictiveSearchResults?.setAttribute('inert', '');
      document.body.classList.remove('open-search');
    }
    open() {
      document.body.classList.add('open-search');
      this.predictiveSearchResults.removeAttribute('inert');
    }
  }
  customElements.define('search-form', SearchForm);
}
